import { Modal } from 'antd'
import classNames from 'classnames'
import { useState } from 'react'
import { toast } from 'sonner'

import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import ModalWithButtons from '@/components/Atoms/ModalWithButtons/ModalWithButtons'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  NotificationPermissionsDto,
  NotificationSubscriptionDto,
  NotificationTypeDto,
  OrganisationShortType,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import SubscriptionClientFilter from './SubscriptionClientFilter'
import {
  buildSubscriptionRequest,
  getRequestTargetType,
  getTargetId,
} from './SubscriptionCreationModal.config'
import cn from './SubscriptionCreationModal.module.scss'
import SubscriptionTargetSection from './SubscriptionTargetSection'
import useSubscriptionTarget from './useSubscriptionTarget'

type SubscriptionCreationModalProps = {
  open: boolean
  setOpen: (open: boolean) => void
  onCreated: (subscription: NotificationSubscriptionDto) => void
  permissions: NotificationPermissionsDto
  types: NotificationTypeDto[]
  isOrganisationSelected?: boolean
}

function SubscriptionCreationModal({
  open,
  setOpen,
  onCreated,
  permissions,
  types,
  isOrganisationSelected = false,
}: SubscriptionCreationModalProps) {
  const currentUserId = useUserInfoStore((state) => state.currentUser?.id)
  const tokenRoles = useUserInfoStore((state) => state.tokenRoles)
  const [modal, contextHolder] = Modal.useModal()

  const [eventType, setEventType] = useState<NotificationTypeDto | null>(null)
  const [allMyClients, setAllMyClients] = useState(false)
  const [selectedClients, setSelectedClients] = useState<
    OrganisationShortType[]
  >([])
  const [saving, setSaving] = useState(false)

  const target = useSubscriptionTarget(modal)

  const availableTypes = types.filter((type) =>
    type.allowedRoles.some((role) => tokenRoles.includes(role.toUpperCase()))
  )
  const withClientFilter =
    permissions.canUseClientFilters && !!eventType?.supportsClientFilters

  const handleSubmit = () => {
    if (!eventType) {
      toast.error('Выберите тип события')
      return
    }
    const targetId = getTargetId({
      canManageGlobal: permissions.canManageGlobal,
      targetType: target.targetType,
      currentUserId,
      selectedUserId: target.selectedUser?.id,
      selectedRole: target.selectedRole,
      chatId: target.chatId,
    })
    if (!targetId) {
      toast.error('Укажите получателя')
      return
    }

    setSaving(true)
    api
      .post(
        '/notifications/subscriptions',
        buildSubscriptionRequest({
          eventType: eventType.code,
          targetType: getRequestTargetType(
            permissions.canManageGlobal,
            target.targetType
          ),
          targetId,
          withClientFilter,
          allMyClients,
          selectedClientIds: selectedClients.map((client) => client.id),
        })
      )
      .then((response) => {
        onCreated(response.data.subscription)
        toast.success('Подписка создана')
        setOpen(false)
      })
      .catch((error) => handleApiError(error, modal))
      .finally(() => setSaving(false))
  }

  return (
    <>
      {contextHolder}
      <ModalWithButtons
        isOrganisationSelected={isOrganisationSelected}
        title="Добавление подписки"
        okText="Создать"
        open={open}
        confirmLoading={saving}
        onOk={handleSubmit}
        onCancel={() => setOpen(false)}
      >
        <div
          className={classNames(
            cn.body,
            isOrganisationSelected && cn.bodySelected
          )}
        >
          <section className={cn.section}>
            <h3 className={cn.sectionTitle}>1. Тип события</h3>
            <HoverElement>
              <AtomSelect
                isOrganisationSelected={isOrganisationSelected}
                openByHover={false}
                outlined
                placeholder="Выберите событие"
                value={eventType?.code}
                options={availableTypes.map((type) => ({
                  value: type.code,
                  label: type.description,
                }))}
                popupClassName={
                  isOrganisationSelected ? cn.popupSelected : cn.popup
                }
                handleChange={(code) =>
                  setEventType(
                    availableTypes.find((type) => type.code === code) ?? null
                  )
                }
              />
            </HoverElement>
          </section>

          {permissions.canManageGlobal && (
            <SubscriptionTargetSection
              target={target}
              isOrganisationSelected={isOrganisationSelected}
              popupClassName={
                isOrganisationSelected ? cn.popupSelected : cn.popup
              }
            />
          )}

          {withClientFilter && (
            <SubscriptionClientFilter
              modal={modal}
              allMyClients={allMyClients}
              onAllMyClientsChange={setAllMyClients}
              selectedClients={selectedClients}
              onSelectedClientsChange={setSelectedClients}
            />
          )}
        </div>
      </ModalWithButtons>
    </>
  )
}

export default SubscriptionCreationModal
